import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { GroupService } from '../../../../lib/services/group.service';
import {
  AvailableRelease,
  GroupInfo,
  GroupRelease,
  GroupReleaseInfos,
} from '../../../../lib/models/group.model';
import {
  CouchDbService,
  DbTypeEnum,
  FetchResponse,
} from '../../../../lib/services/couch-db.service';
import { ActivityIndicatorWithMessages } from '../../../../lib/activity-indicator-with-messages/activity-indicator-with-messages.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { LocalSettingsService } from '../../../../lib/services/local-settings.service';
import { DesktopService } from '../../../../lib/services/desktop.service';
import {
  AccessMatrixService,
  ModuleTypeEnum,
  RouteTypeEnum,
  SubModuleTypeEnum,
  VersionType,
} from '../../../../lib/services/access-matrix.service';
import { Group } from '@icure/api';
import { WarmupService } from '../../../../lib/services/warmup.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReleaseSelectorDialogComponent } from '../dialogs/release-selector-dialog/release-selector-dialog.component';
import { GroupCreationDialogComponent } from '../dialogs/group-creation-dialog/group-creation-dialog.component';
import { GroupCreationReturnDialogComponent } from '../dialogs/group-creation-return-dialog/group-creation-return-dialog.component';
import { GroupOverrideDialogComponent } from '../../../features/licenses/components/group-override-dialog/group-override-dialog.component';
import { LicenseService } from '../../../shared/license-list/services/license.service';
import { LoggerService } from '../../../shared/logger/services/logger.service';
import { RecentActivityService } from '../../../shared/recent-activity/services/recent-activity.service';

export const SHARED_DB =
  'ms-medispring-free-reference-8227c0f3-6e6f-401d-8e8a-a94b199636be';
export const FREE_DB = 'ms-free-super-admin';
export const CL1 = 'couch-cluster-01';
export const CL2 = 'couch-cluster-02';

export enum RolloutTypeEnum {
  STAGED = 'staged',
  ALL = 'all',
}

export enum UpdateTypeEnum {
  ALLOWED = 'allowed',
  PARTIALLY_ALLOWED = 'partiallyAllowed',
  NOT_ALLOWED = 'notAllowed',
}

export enum MessageTypeEnum {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
}

export enum ReleaseActionEnum {
  ADD = 'add',
  REMOVE = 'remove',
}

@Component({
  selector: 'ms-database-group',
  templateUrl: './database-group.component.html',
  styleUrls: ['./database-group.component.scss'],
  standalone: false,
})
export class DatabaseGroupComponent implements OnInit {
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  @Output() public finish: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public onError: EventEmitter<boolean> = new EventEmitter();

  private allGroups: Array<Group> = [];
  private allGroupsReleaseInfos: GroupRelease[] = [];
  private availableReleaseVersions: Array<string> = [];
  public groupInfosDataSource: MatTableDataSource<GroupInfo> =
    new MatTableDataSource<GroupInfo>();
  public groupInfos: Array<GroupInfo> = [];
  public freeColumns: string[] = [
    'name',
    'groupId',
    'override',
    'servers',
    'actions',
  ];
  public fullColumns: string[] = [
    'name',
    'groupId',
    'override',
    'servers',
    'active',
    'authorizedVersion',
    'actions',
  ];
  public displayedColumns: string[] = [];
  public isLoading: boolean = false;
  public isWarmUp: boolean = false;
  public messages: Array<ActivityIndicatorWithMessages> = [];
  public warmUpMessages: Array<ActivityIndicatorWithMessages> = [];
  public versionType: VersionType = VersionType.FULL;
  public selectedGroupId: string = '';

  public krakenUser: boolean = false;
  public selectedFilter: string = '';
  public activeGroups: Array<string> = [];
  private recentActivityMatchingGroupIds: Set<string> = new Set<string>();

  constructor(
    private groupService: GroupService,
    private couchDbService: CouchDbService,
    private localSettingsService: LocalSettingsService,
    private desktopService: DesktopService,
    public accessMatrixService: AccessMatrixService,
    private warmUpService: WarmupService,
    private snackBar: MatSnackBar,
    private releaseSelectorDialog: MatDialog,
    private dialog: MatDialog,
    public licenseService: LicenseService,
    public recentActivityService: RecentActivityService,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    this.versionType = this.localSettingsService.version;
    this.displayedColumns =
      this.versionType === VersionType.FREE
        ? this.freeColumns
        : this.fullColumns;
    this.initialize();
  }

  private async initialize(): Promise<void> {
    this.messages = [];
    this.isLoading = true;
    this.groupInfosDataSource = new MatTableDataSource();
    this.allGroups = [];
    this.groupInfos = [];

    this.messages.push({ message: 'Récupération des groupes', isDone: false });
    if (this.localSettingsService.listOfGroups?.length) {
      this.allGroups = this.localSettingsService.listOfGroups;
    } else {
      try {
        this.allGroups = await this.groupService.getAllGroups();
      } catch (error: unknown) {
        this.logger.warn('Failed to load groups', error);
        this.allGroups = [];
      }
    }

    await this.recentActivityService.refreshActiveGroups();

    const oauthIdToken = this.localSettingsService.oauthIdToken;
    if (oauthIdToken) {
      void this.licenseService
        .loadGroupOverrideList(oauthIdToken)
        .catch((error: unknown): void => {
          this.logger.warn('Failed to load group license overrides', error);
        });
    }

    if (
      this.accessMatrixService?.isAvailableModule(
        RouteTypeEnum.DATABASE,
        ModuleTypeEnum.DATABASE_DESKTOP_API,
      )
    ) {
      const activeGroupsResp = await this.desktopService.listGroups();
      this.activeGroups =
        activeGroupsResp?.status?.status === 200
          ? activeGroupsResp?.content
          : [];
      const getAvailableReleaseVersionsResponse: FetchResponse =
        await this.desktopService?.getAvailableReleaseVersions();
      this.availableReleaseVersions =
        (getAvailableReleaseVersionsResponse?.content ?? []) as Array<string>;
      this.allGroupsReleaseInfos = await this.desktopService.getReleaseInfos(
        this.availableReleaseVersions.filter((version: string) =>
          version.startsWith('3'),
        ),
      );
    }

    this.allGroups.forEach((group: Group) => {
      const availableReleases: AvailableRelease[] =
        this.getUpdateAuthorization(group);
      this.groupInfos.push({
        groupId: group.id ?? '',
        groupPassword: group.password ?? '',
        name: group.name ?? '',
        superGroup: group.superGroup === FREE_DB ? 'Free' : 'Full',
        sharedEntities: {
          code: this.checkSharedEntity(group.sharedEntities?.['Code'] ?? ''),
          insurance: this.checkSharedEntity(
            group.sharedEntities?.['Insurance'] ?? '',
          ),
          tarification: this.checkSharedEntity(
            group.sharedEntities?.['Tarification'] ?? '',
          ),
        },
        serversHr: this.checkCluster(group.servers ?? []),
        servers: (group.servers ?? []).join(', '),
        groupRaw: group,
        releaseVersion: availableReleases,
        isActive: this.activeGroups.includes(group.id ?? ''),
        isKrakenVersion: !!availableReleases?.find(
          (ar) =>
            ar?.authorization === UpdateTypeEnum.ALLOWED ||
            ar?.authorization === UpdateTypeEnum.PARTIALLY_ALLOWED,
        ),
      });
    });
    this.groupInfosDataSource = new MatTableDataSource<GroupInfo>(
      this.groupInfos,
    );
    this.groupInfosDataSource.filterPredicate = (
      data: GroupInfo,
      filter: string,
    ): boolean => this.matchesGroupFilter(data, filter);
    this.initializeGridOptions();
    this.filterChanged();
    this.isLoading = false;
  }

  private getUpdateAuthorization(group: Group) {
    return this.allGroupsReleaseInfos
      ?.map((groupRelease: GroupRelease) => {
        const gri: GroupReleaseInfos | null =
          groupRelease?.groupIds?.find((gri) => gri?.groupId === group?.id) ||
          null;
        let authorization: UpdateTypeEnum = UpdateTypeEnum.NOT_ALLOWED;
        if (gri) {
          if (groupRelease?.rolloutType === RolloutTypeEnum?.STAGED) {
            authorization =
              gri?.onlyDEV === false &&
              gri?.onlyQA === false &&
              gri?.onlyConfigIds?.length === 0
                ? UpdateTypeEnum.ALLOWED
                : gri?.onlyDEV !== false ||
                    gri?.onlyQA !== false ||
                    gri?.onlyConfigIds?.length !== 0
                  ? UpdateTypeEnum.PARTIALLY_ALLOWED
                  : UpdateTypeEnum.NOT_ALLOWED;
          } else if (groupRelease?.rolloutType === RolloutTypeEnum?.ALL) {
            authorization = UpdateTypeEnum.ALLOWED;
          }
        }
        return {
          version: groupRelease?.version,
          authorization: authorization,
        };
      })
      ?.filter((x) => !!x);
  }

  private initializeGridOptions() {
    //Use this to reinitialize sorting
    this.sort.sort({ id: '', start: 'desc' } as MatSortable);
    this.sort.sort({ id: 'name', start: 'asc' } as MatSortable);
    this.groupInfosDataSource.sort = this.sort;
    this.groupInfosDataSource.sortingDataAccessor = (
      item: GroupInfo,
      property: string,
    ): string | number => {
      switch (property) {
        case 'creationDateHr': {
          return '';
        }
        default: {
          const value = (item as unknown as Record<string, string | number>)[
            property
          ];
          return value ?? '';
        }
      }
    };
    this.groupInfosDataSource.paginator = this.paginator;
  }

  private checkSharedEntity(entity: string): boolean {
    return entity?.includes(SHARED_DB) || false;
  }

  private checkCluster(servers: Array<string>): string {
    return servers
      ?.map((ser) => (ser.includes(CL1) ? 'Cluster 01' : 'Cluster 02'))
      .join(',');
  }

  public async connectAndRedirect(groupInfo: GroupInfo) {
    const groupId = groupInfo?.groupId;
    const groupPassword = groupInfo?.groupPassword;
    const server = groupInfo?.servers?.[0];
    if (!groupId || !groupPassword || !server) {
      return;
    }

    const session: FetchResponse = await this.couchDbService.createSession(
      groupId,
      groupPassword,
      server,
    );
    if (session?.status?.status === 200) {
      await this.couchDbService.connectToDb(
        groupId,
        groupPassword,
        server,
        DbTypeEnum.BASE,
      );
    }
  }

  filterChanged(): void {
    let filteredGroup: Array<GroupInfo> = [];
    filteredGroup = this.krakenUser
      ? this.groupInfos?.filter(
          (data: GroupInfo) => data?.isKrakenVersion === true,
        )
      : this.groupInfos;

    this.groupInfosDataSource.data = filteredGroup;

    const searchFilter = this.selectedFilter.trim();
    const normalizedSearchFilter = searchFilter.toLowerCase();
    this.recentActivityMatchingGroupIds = normalizedSearchFilter
      ? new Set(
          this.recentActivityService.findMatchingGroupIds(
            normalizedSearchFilter,
          ),
        )
      : new Set<string>();
    this.groupInfosDataSource.filter = searchFilter;
  }

  private matchesGroupFilter(data: GroupInfo, filter: string): boolean {
    const normalizedFilter = filter.trim().toLowerCase();

    if (!normalizedFilter) {
      return true;
    }

    return (
      data?.name?.toLowerCase()?.includes(normalizedFilter) === true ||
      data?.groupId?.toLowerCase()?.includes(normalizedFilter) === true ||
      this.recentActivityMatchingGroupIds.has(data?.groupId ?? '')
    );
  }

  public get RouteTypeEnum() {
    return RouteTypeEnum;
  }

  public get ModuleTypeEnum() {
    return ModuleTypeEnum;
  }

  public get SubModuleTypeEnum() {
    return SubModuleTypeEnum;
  }

  get UpdateTypeEnum() {
    return UpdateTypeEnum;
  }

  public async activeWarmUp(groupInfo: GroupInfo) {
    if (groupInfo?.groupId && groupInfo?.servers) {
      let index = 0;
      this.isWarmUp = true;
      this.selectedGroupId = groupInfo?.groupId;
      this.warmUpMessages = [];
      for (const entity of this.warmUpService.getEntitiesToWarmUp()) {
        this.warmUpMessages.push({
          id: index.toString(),
          message: `Vue: ${entity.entity}`,
          isDone: false,
        });
        const result = await this.warmUpService.updateDesignDoc(
          entity.entity,
          groupInfo?.groupId,
          groupInfo?.servers,
        );
        if (result?.status?.status === 200) {
          const message = this.warmUpMessages.find(
            (m) => m.id === index.toString(),
          );
          if (message) {
            message.isDone = true;
          }
        } else {
          this.logger.warn(
            `Error while updating design doc for entity ${entity.entity} : ${result?.status?.statusText}`,
          );
        }
        index += 1;
      }
      this.isWarmUp = false;
    }
  }

  public openVersionSelector(groupInfo: GroupInfo, action: ReleaseActionEnum) {
    const availableVersions =
      action === ReleaseActionEnum.ADD
        ? this.availableReleaseVersions
        : groupInfo?.releaseVersion?.map((v) => v?.version);
    const dialogRef = this.releaseSelectorDialog.open(
      ReleaseSelectorDialogComponent,
      {
        data: {
          availableVersions: availableVersions,
          action: action,
        },
        height: '400px',
        width: '600px',
      },
    );

    dialogRef.afterClosed().subscribe((version) => {
      if (version) {
        if (action === ReleaseActionEnum.ADD) {
          this.authorizeGroupInRelease(groupInfo, version);
        } else if (action === ReleaseActionEnum.REMOVE) {
          this.removeGroupFromRelease(groupInfo, version);
        }
      }
    });
  }

  public async authorizeGroupInRelease(groupInfo: GroupInfo, version: string) {
    this.isLoading = true;
    const groupId = groupInfo?.groupId;
    if (!groupId) {
      this.isLoading = false;
      return;
    }

    const resp: FetchResponse | undefined =
      await this.desktopService.addGroupIdToRelease(groupId, version);
    if (resp?.status?.status === 200) {
      this.displayWarningMessage(
        `Le groupId: ${groupInfo?.groupId} à bien été activé dans la release`,
        MessageTypeEnum.SUCCESS,
      );
      await this.initialize();
    } else {
      this.displayWarningMessage(
        `Erreur lors de l'activation du groupId: ${groupInfo?.groupId}`,
        MessageTypeEnum.ERROR,
      );
    }
    this.isLoading = false;
  }

  public async removeGroupFromRelease(groupInfo: GroupInfo, version: string) {
    this.isLoading = true;
    const groupId = groupInfo?.groupId;
    if (!groupId) {
      this.isLoading = false;
      return;
    }

    const resp: FetchResponse | undefined =
      await this.desktopService.removeGroupIdFromRelease(groupId, version);
    if (resp?.status?.status === 200) {
      this.displayWarningMessage(
        `Le groupId: ${groupInfo?.groupId} à bien été supprimé de la release`,
        MessageTypeEnum.SUCCESS,
      );
      await this.initialize();
    } else {
      this.displayWarningMessage(
        `Erreur lors de la suppression du groupId: ${groupInfo?.groupId}`,
        MessageTypeEnum.ERROR,
      );
    }
    this.isLoading = false;
  }

  private displayWarningMessage(message: string, messageType: MessageTypeEnum) {
    this.snackBar.open(message, '', {
      duration: 5000,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: [messageType],
    });
  }

  public get releaseActionEnum() {
    return ReleaseActionEnum;
  }

  public async openCreateGroupDialog() {
    const openCreateGroupDialogRef = this.dialog.open(
      GroupCreationDialogComponent,
      {
        height: '650px',
        width: '1000px',
      },
    );

    openCreateGroupDialogRef.afterClosed().subscribe((result) => {
      if (
        result?.group?.id &&
        result?.parent?.hcp?.id &&
        result?.mainUser?.hcp?.id
      ) {
        this.initialize();
        this.openResultDialog(result);
      } else {
        this.displayWarningMessage(
          `La création du groupe a été annulée ou une erreur est survenue`,
          MessageTypeEnum.ERROR,
        );
      }
    });
  }

  public openResultDialog(result: unknown): void {
    this.logger.log(result);
    void this.dialog.open(GroupCreationReturnDialogComponent, {
      maxWidth: '92vw',
      data: { creationResult: result },
    });
  }

  public openGroupOverrideDialog(groupInfo: GroupInfo): void {
    const groupId = groupInfo?.groupId;
    if (!groupId) {
      return;
    }

    const dialogRef = this.dialog.open(GroupOverrideDialogComponent, {
      width: '560px',
    });

    dialogRef.componentInstance.lockGroupId(groupId);

    dialogRef
      .afterClosed()
      .subscribe((result: { created?: boolean } | undefined): void => {
        if (result?.created) {
          void this.initialize();
        }
      });
  }

  public async unblockGroup(groupInfo: GroupInfo): Promise<void> {
    const groupId = groupInfo?.groupId;
    if (!groupId) {
      return;
    }

    const token: string | null = this.localSettingsService.oauthIdToken;
    if (!token) {
      return;
    }

    await this.licenseService.deleteGroupOverride(token, groupId);
    await this.initialize();
  }

  public canShowGroupOverrideActions(groupId: string): boolean {
    const overrideType = this.licenseService.getGroupOverrideType(groupId);

    return overrideType !== 'IN_ORDER';
  }

  public canUnblockGroup(groupId: string): boolean {
    const overrideType = this.licenseService.getGroupOverrideType(groupId);

    return overrideType !== null && overrideType !== 'IN_ORDER';
  }
}
